import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faVial, 
    faAngleDown, 
    faAngleRight, 
    faExclamationTriangle,
    faInfoCircle,
    faFlask,
    faSortAmountUp,
    faSortAmountDown
} from '@fortawesome/free-solid-svg-icons';
import './ProteomicsCard.css';

/**
 * Specialized component for rendering Proteomics analysis data
 */
const ProteomicsCard = ({ data, animationDelay }) => {
    // Debug logging
    console.log('Proteomics Card received data:', data);
    
    const [expanded, setExpanded] = useState(false);
    const [activeSection, setActiveSection] = useState('abnormal');
    
    // Early return if no data
    if (!data) {
        console.error('Proteomics Card received no data');
        return null;
    }
    
    // Get details safely
    const details = data.details || {};
    const summary = details.summary || {};
    const abnormalProteins = details.abnormal_proteins || [];
    
    // Deduplicate proteins (the sample data has duplicates)
    const uniqueAbnormalProteins = abnormalProteins.reduce((acc, protein) => {
        const exists = acc.find(p => p.assay === protein.assay && p.status === protein.status);
        if (!exists) {
            acc.push(protein);
        }
        return acc;
    }, []);
    
    // Calculate category counts
    const categoryCountMap = {};
    uniqueAbnormalProteins.forEach(protein => {
        if (protein.category) {
            categoryCountMap[protein.category] = (categoryCountMap[protein.category] || 0) + 1;
        }
    });
    
    // Get status color information
    const hasAbnormalProteins = summary.abnormal_proteins_count > 0;
    const statusColor = hasAbnormalProteins ? '#64b5f6' : '#64b5f6';
    const status = hasAbnormalProteins ? 'Abnormal' : 'Normal';
    
    // Create summary pills
    const summaries = [];
    
    if (uniqueAbnormalProteins.length > 0) {
        summaries.push({
            text: `${uniqueAbnormalProteins.length} abnormal protein${uniqueAbnormalProteins.length !== 1 ? 's' : ''}`,
            isWarning: false
        });
        
        // Add category information if available
        const categories = Object.keys(categoryCountMap);
        if (categories.length > 0) {
            const topCategory = categories.sort((a, b) => 
                categoryCountMap[b] - categoryCountMap[a])[0];
            
            summaries.push({
                text: `${categoryCountMap[topCategory]} in ${topCategory}`,
                isWarning: false
            });
        }
    }
    
    return (
        <div className="s2-proteo-card" style={{ animationDelay }}>
            <div className="s2-proteo-header" onClick={() => setExpanded(!expanded)}>
                <div className="s2-proteo-icon">
                    <FontAwesomeIcon 
                        icon={faVial} 
                        className="s2-proteo-icon-vial"
                    />
                </div>
                <div className="s2-proteo-title-container">
                    <div className="s2-proteo-title">Proteomics Analysis</div>
                    
                    {/* Proteomics Summary Section */}
                    {summaries.length > 0 && (
                        <div className="s2-proteo-summary">
                            {summaries.map((summary, index) => (
                                <div 
                                    key={index} 
                                    className={`s2-proteo-pill ${summary.isWarning ? 's2-proteo-pill-warning' : ''}`}
                                >
                                    {summary.text}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                <div className="s2-proteo-expand-icon">
                    <FontAwesomeIcon 
                        icon={expanded ? faAngleDown : faAngleRight} 
                    />
                </div>
            </div>
            
            {expanded && (
                <div className="s2-proteo-content s2-anim-fadeIn">
                    {/* WGS-style Summary Section */}
                    <div className="s2-proteo-wgs-summary">
                        <div className="s2-proteo-summary-row">
                            <div className="s2-proteo-summary-item">
                                <div className="s2-proteo-summary-label">Total Proteins</div>
                                <div className="s2-proteo-summary-value">
                                    {summary.total_proteins_analyzed || 0}
                                </div>
                            </div>
                            
                            <div className="s2-proteo-summary-item">
                                <div className="s2-proteo-summary-label">Abnormal Proteins</div>
                                <div className="s2-proteo-summary-value">
                                    <span className={uniqueAbnormalProteins.length > 0 ? 's2-proteo-abnormal' : ''}>
                                        {uniqueAbnormalProteins.length || 0}
                                    </span>
                                </div>
                            </div>
                            
                            <div className="s2-proteo-summary-item">
                                <div className="s2-proteo-summary-label">Categories</div>
                                <div className="s2-proteo-summary-value">
                                    {summary.categories_analyzed || 0}
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    {/* Tab Navigation */}
                    <div className="s2-proteo-tabs">
                        <div 
                            className={`s2-proteo-tab ${activeSection === 'abnormal' ? 's2-proteo-tab-active' : ''}`}
                            onClick={() => setActiveSection('abnormal')}
                        >
                            <FontAwesomeIcon icon={faExclamationTriangle} className="s2-proteo-tab-icon" />
                            Abnormal Proteins
                        </div>
                    </div>
                    
                    {/* Abnormal Proteins Section */}
                    {activeSection === 'abnormal' && (
                        <div className="s2-proteo-abnormal-section">
                            {uniqueAbnormalProteins.length > 0 ? (
                                <div className="s2-proteo-abnormal-grid">
                                    {uniqueAbnormalProteins.map((protein, index) => {
                                        // Parse reference range
                                        const rangeMatch = protein.reference_range.match(/\((.*?),\s*(.*?)\)/);
                                        const min = rangeMatch ? parseFloat(rangeMatch[1]) : 0;
                                        const max = rangeMatch ? parseFloat(rangeMatch[2]) : 1;
                                        
                                        // Calculate percentage for visualization
                                        const value = parseFloat(protein.patient_value);
                                        const normalizedPercentage = Math.min(100, Math.max(0, ((value - min) / (max - min)) * 100));
                                        
                                        // Determine status color
                                        const statusColor = protein.status === 'Low' ? '#f44336' : 
                                                           protein.status === 'High' ? '#ff9800' : '#64b5f6';
                                        
                                        return (
                                            <div key={index} className="s2-proteo-protein-item">
                                                <div className="s2-proteo-protein-header">
                                                    <div className="s2-proteo-protein-name">
                                                        {protein.protein_name}
                                                        <div className="s2-proteo-protein-assay">{protein.assay}</div>
                                                    </div>
                                                    <div className="s2-proteo-protein-category">
                                                        {protein.category}
                                                    </div>
                                                </div>
                                                
                                                <div className="s2-proteo-protein-status" style={{ color: statusColor }}>
                                                    {protein.status}
                                                </div>
                                                
                                                <div className="s2-proteo-protein-bar-container">
                                                    <div className="s2-proteo-protein-range">
                                                        <span className="s2-proteo-range-min">{min.toFixed(6)}</span>
                                                        <span className="s2-proteo-range-max">{max.toFixed(6)}</span>
                                                    </div>
                                                    <div className="s2-proteo-range-bar">
                                                        <div 
                                                            className="s2-proteo-patient-marker"
                                                            style={{ 
                                                                left: `${normalizedPercentage}%`,
                                                                backgroundColor: statusColor
                                                            }}
                                                        ></div>
                                                    </div>
                                                    <div className="s2-proteo-protein-value">
                                                        Patient: <span style={{ fontWeight: 'bold' }}>{value.toFixed(6)}</span>
                                                    </div>
                                                </div>
                                                
                                                {protein.brief_explanation && (
                                                    <div className="s2-proteo-protein-explanation">
                                                        {protein.brief_explanation}
                                                    </div>
                                                )}
                                                
                                                {protein.related_diseases && protein.related_diseases.length > 0 && (
                                                    <div className="s2-proteo-protein-diseases">
                                                        <div className="s2-proteo-diseases-label">Related Conditions:</div>
                                                        <div className="s2-proteo-diseases-list">
                                                            {protein.related_diseases.map((disease, idx) => (
                                                                <div key={idx} className="s2-proteo-disease-item">
                                                                    {disease}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="s2-proteo-no-data">No abnormal proteins detected</div>
                            )}
                        </div>
                    )}
                </div>
            )}
            
            {data.source && (
                <div className="s2-proteo-footer">
                    <span className="s2-proteo-source">
                        <FontAwesomeIcon icon={faInfoCircle} className="s2-proteo-source-icon" />
                        {data.source}
                    </span>
                </div>
            )}
        </div>
    );
};

export default ProteomicsCard;
